import { QueryKey } from '@tanstack/react-query';
import { CartItem as CartItemModel } from '@utils/generate-cart-item';

export type Nullable<T> = T | null | undefined;

export type User = {
  id: number;
  email_verified: boolean;
  role: string;
  name: string;
  email: string;
  phone: string;
  is_active: boolean;
  contact: UserContact[];
  addresses: CustomerAddress[];
};

export type UserContact = {
  id: number;
  title: string;
  default: boolean;
  number: string;
};

export type AddressDetails = {
  street_address: string;
  country: string;
  city: string;
  state: string;
  zip: string;
};

export type CollectionsQueryOptionsType = {
  text?: string;
  collection?: string;
  status?: string;
  limit?: number;
};

export type CategoriesQueryOptionsType = {
  text?: string;
  category?: string;
  status?: string;
  limit?: number;
};
export type ProductsQueryOptionsType = {
  type: string;
  text?: string;
  category?: string;
  status?: string;
  limit?: number;
};
export type QueryOptionsType = {
  text?: string;
  category?: string;
  status?: string;
  limit?: number;
};

export type QueryParamsType = {
  queryKey: QueryKey;
  pageParam?: string;
};
export type Attachment = {
  id: string | number;
  thumbnail: string;
  original: string;
};
export type Category = {
  id: number | string;
  name_de: string;
  name_en: string;
  name_fr: string;
  name_he: string;
  slug: string;
  details?: string;
  image?: Attachment;
  icon?: string;
  children?: [Category];
  products?: Product[];
  productCount?: number;
  [key: string]: unknown;
};
export type Collection = {
  id: number | string;
  name: string;
  slug: string;
  details?: string;
  image?: Attachment;
  icon?: string;
  products?: Product[];
  productCount?: number;
};
export type Brand = {
  id: number | string;
  name: string;
  slug: string;
  image?: Attachment;
  [key: string]: unknown;
};
export type FilterItem = {
  id: number | string;
  key?: string;
  label_de: string;
  label_en: string;
  label_fr: string;
  label_he: string;
  slug: string;
  icon?: string;
  children: FilterItem[];
};
export type Tag = {
  id: string | number;
  name: string;
  slug: string;
};

export type ProductBatch = {
  id: number;
  product_id: string;
  description: string;
  batch_id: string;
  expiration_date: string;
  sale_percentage?: number;
  quantity: number;
};
export type Product = {
  id: number | string;
  barcode: number | string;
  name_de: string;
  name_en: string;
  name_fr: string;
  name_he: string;
  manufacturer_name: Nullable<string>;
  origin_country: Nullable<string>;
  slug: string;
  price: number;
  quantity: number;
  sold: number;
  unit: string;
  items_per_unit: number;
  sale_price?: number;
  min_price?: number;
  max_price?: number;
  image: Nullable<string>;
  sku?: string;
  gallery?: Attachment[];
  category?: Category;
  tag?: Tag[];
  meta?: any[];
  brand?: Brand;
  description?: string;
  description_en?: string;
  description_fr?: string;
  description_he?: string;
  kosher?: string;
  kosher_label?: string;
  kosher_pesach?: string;
  managed_batches?: boolean;
  batches: ProductBatch[];
  coming_soon: boolean;
  [key: string]: unknown;
};

export const getProductStock = (product: Product) => product?.quantity;

export const getNameForLanguage = (
  item: Nullable<Product | Category | CartItemModel | OrderItem>,
  lang: string,
) => {
  const l: 'he' | 'en' | 'de' | 'fr' = lang as any;
  return item ? ((item[`name_${l}`] ?? item.name_de) as string) : '';
};

export const getDescriptionForLanguage = (
  item: Nullable<Product>,
  lang: string,
) => {
  const l: 'he' | 'en' | 'de' | 'fr' = lang as any;
  if (!item) return '';
  const langDescription = item[`description_${l}`] as string | undefined;
  return langDescription && langDescription.trim() !== ''
    ? langDescription
    : (item.description ?? '');
};

export const getFilterNameForLanguage = (
  item: Nullable<FilterItem>,
  lang: string,
) => {
  const l: 'he' | 'en' | 'de' | 'fr' = lang as any;
  return item ? ((item[`label_${l}`] ?? item.label_de) as string) : '';
};

export const getProductStockForBatch = (batch: ProductBatch) => batch?.quantity;

export const getKosherImageURL = (kosherType: string) =>
  `/assets/images/kosher/${kosherType.toUpperCase()}.png`;

export type OrderItem = {
  id: number | string;
  name_de: string;
  name_en: string;
  name_fr: string;
  name_he: string;
  price: number;
  quantity: number;
};
export type Order = {
  id: string | number;
  name: string;
  slug: string;
  products: OrderItem[];
  total: number;
  tracking_number: string;
  created_at: string;
  customer: {
    id: number;
    email: string;
  };
  shipping_fee: number;
  payment_gateway: string;
  shipping_address: AddressDetails;
  status: OrderStatus;
  comment: Nullable<string>;
};

export type CustomerAddress = {
  address_name: string;
  street_address: string;
  country: string;
  city: string;
  state: string;
  zip: string;
};

export type Customer = {
  id: string;
  name: string;
  phone: string;
  addresses: CustomerAddress[];
};

export type OrderStatus = {
  id: number;
  name: string;
  serial: number;
  created_at: string;
  updated_at: string;
};

export type ShopsQueryOptionsType = {
  text?: string;
  shop?: Shop;
  status?: string;
  limit?: number;
};

export type Shop = {
  id: string | number;
  owner_id: string | number;
  owner_name: string;
  address: string;
  phone: string;
  website: string;
  ratings: string;
  name: string;
  slug: string;
  description: string;
  cover_image: Attachment;
  logo: Attachment;
  socialShare: any;
  created_at: string;
  updated_at: string;
};
